import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { noop } from 'ui-components/default-props/noop';
import { NotificationService } from 'ui-components/services/notification-service';

import { label } from '../../../../../src/store/printing/document-type-epic-helpers/label';

import { DocumentType } from '../../../../enums/document-type';
import { DocumentTypePropType } from '../../../../prop-types/document-type';
import { DocumentService } from '../../../../services/document-service';
import {
  documentDownloadStarted,
  markLabelPrinted,
  markPackingSlipPrinted,
  markPickListPrinted,
} from '../../../../store/printing/print.actions';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { PrintModalHeader } from '../../../../components/modals/print-modal/print-modal-content/print-modal-header';
import { usePrinters } from '../../../../hooks/printers/use-printers';

import * as PrintApi from '../../../../store/printing/print.api';
import { BrowserPrintTip } from './browser-print-tip';
import { HeaderMessage } from './header-message';
import { RememberPrintSetting } from './remember-print-setting';
import { ShipStationPrint } from './shipstation-print';
import style from './print-modal-content.module.less';
import { TemplateSection } from './template-section';

const PrintModalContentComponent = ({
  documentUrl,
  templates = emptyArray,
  selectedTemplate = emptyObject,
  changeTemplate = noop,
  closeModal,
  downloadAndPrintDisabled = false,
  documentType = '',
  setPrintDestination,
  printDestination,
  rememberPrintSettings = false,
  setRememberPrintSettings,
  fulfillmentPlanIds = emptyArray,
  fulfillmentIds = emptyArray,
  onFileLoaded,
  documentCount,
  fulfillmentPlanSetIds = emptyArray,
}) => {
  const dispatch = useDispatch();

  const { printers, hasInstalledShipStationConnect } = usePrinters();

  const downloadDocument = useCallback(() => {
    const notificationError = label.getAsyncPrintedStatusUpdateError();

    const getDownloadUrl = () => {
      const url = new URL(documentUrl, window.location.origin);
      url.searchParams.append('download', '1');

      if (documentType === DocumentType.BatchLabel.identifier || documentType === DocumentType.BatchPackingSlip.identifier) {
        url.searchParams.append('markAsPrinted', 'true');
      }

      return `${url.pathname}${url.search}`;
    };

    const download = () => {
      dispatch(documentDownloadStarted(documentType, documentCount));

      const downloadUrl = getDownloadUrl();
      DocumentService.downloadFile(downloadUrl);

      if (documentType === DocumentType.Label.identifier) {
        dispatch(markLabelPrinted(fulfillmentPlanIds, fulfillmentIds, true));
      } else if (documentType === DocumentType.PackingSlip.identifier) {
        dispatch(markPackingSlipPrinted(fulfillmentPlanIds, true));
      } else if (documentType === DocumentType.BatchPackingSlip.identifier) {
        PrintApi.markFullfillmentPlanSetsPackingSlipPrinted(fulfillmentPlanSetIds, true).catch(() => {
          NotificationService.showNotification(notificationError);
        });
      } else if (documentType === DocumentType.PickList.identifier) {
        dispatch(markPickListPrinted(fulfillmentPlanIds, true));
      } else if (documentType === DocumentType.BatchPickList.identifier) {
        PrintApi.markFulfillmentPlanSetsPickListPrinted(fulfillmentPlanSetIds, true).catch(() => {
          NotificationService.showNotification(notificationError);
        });
      }
    };

    download();

    closeModal();
  }, [closeModal, dispatch, documentCount, documentType, documentUrl, fulfillmentIds, fulfillmentPlanIds, fulfillmentPlanSetIds]);

  const showTemplateSection =
    documentType === DocumentType.PackingSlip.identifier || documentType === DocumentType.BatchPackingSlip.identifier;

  return (
    <div className={style['print-modal-content']}>
      <div className={style.header}>
        <PrintModalHeader
          closeModal={closeModal}
          headerMessage={<HeaderMessage documentCount={documentCount} documentType={documentType} />}
        />
      </div>
      <div className={style.body}>
        <div className={style['left-section']}>
          {downloadAndPrintDisabled && <LoadingIndicator />}
          <iframe
            data-testid='printframe'
            id='printframe'
            onLoad={onFileLoaded}
            src={`${documentUrl}#toolbar=1&view=fit`}
            title={documentUrl}
          />
        </div>
        <div className={style['right-section']}>
          {showTemplateSection && (
            <TemplateSection
              fulfillmentPlanIds={fulfillmentPlanIds}
              onTemplateSelected={changeTemplate}
              selectedTemplate={selectedTemplate}
              templates={templates}
            />
          )}
          <div className={style['print-options']}>
            <div className={style['top-level-heading']}>
              <FormattedMessage defaultMessage='Print Options' description='printModal.printOptions' id='GY7FD7' />
            </div>
            <BrowserPrintTip downloadAndPrintDisabled={downloadAndPrintDisabled} onDownload={downloadDocument} />
            <ShipStationPrint
              hasInstalledShipStationConnect={hasInstalledShipStationConnect}
              printDestination={printDestination}
              printers={printers}
              setPrintDestination={setPrintDestination}
            />
          </div>
          <RememberPrintSetting
            documentType={documentType}
            rememberPrintSettings={rememberPrintSettings}
            setRememberPrintSettings={setRememberPrintSettings}
          />
        </div>
      </div>
    </div>
  );
};

export const PrintModalContent = memo(PrintModalContentComponent);

PrintModalContent.propTypes = {
  documentUrl: PropTypes.string.isRequired,
  templates: PropTypes.array,
  selectedTemplate: PropTypes.object,
  changeTemplate: PropTypes.func,
  documentType: DocumentTypePropType,
  closeModal: PropTypes.func.isRequired,
  printDestination: PropTypes.object,
  setPrintDestination: PropTypes.func.isRequired,
  rememberPrintSettings: PropTypes.bool,
  setRememberPrintSettings: PropTypes.func.isRequired,
  fulfillmentPlanIds: PropTypes.array,
  fulfillmentIds: PropTypes.array,
  downloadAndPrintDisabled: PropTypes.bool,
  onFileLoaded: PropTypes.func.isRequired,
  documentCount: PropTypes.number.isRequired,
  fulfillmentPlanSetIds: PropTypes.array,
};
